/**
 * @author Maria Osama
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2018-07-02 12:42:30
 * @description Implementation of the TermsAndConditions React component
 * @filename terms-and-conditions.tsx
 */
import React from "react";
import { Router } from "../../../utilities/router";

/**
 *
 * A React component that renders the Terms and Conditions.
 *
 * @extends {React.Component}
 */
export class TermsAndConditions extends React.Component {
  /**
   * Returns a ReactNode containing the rendered component.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered component.
   */
  render(): React.ReactNode {
    return (
      <div id="TermsAndConditions" className="external inner-pages">
        <h1 className="title">Terms & Conditions</h1>
        <div className="introduction">
          <h4>
            Effective Date: April 24, 2017
            <br />
            Welcome to Radivision!
            <br />
            Read carefully before browsing the platform.
          </h4>
          <p>
            Accessing or using the platform indicates that you have read these
            T&amp;C and you agree to follow and be bound by the T&amp;C then in
            force.
          </p>
          <p>YOU CANNOT USE THE PLATFORM IF YOU DO NOT ACCEPT THESE T&amp;C.</p>
        </div>
        <div className="content">
          <ol>
            <li>
              <h5>Agreement and Consent</h5>
              <span>
                <p>
                  The following provisions, including any other documents that
                  are referenced in, or linked from these provisions set forth
                  the provisions of a legal agreement (“Terms &amp; Conditions”
                  or “T&amp;C”) between each individual who has access to the
                  Platform (“user”, "you" or "your") and Radivision, Inc.
                  “Radivision”, "we", or "Company"). They govern all access to,
                  and use of the Platform. We retain the right, at our sole
                  discretion, to deny access to the Platform, to anyone, at any
                  time and for any reason, including but not limited to, for
                  violation of these T&amp;C.
                </p>
                <h5>Terms Applicable to Specific Areas of the Platform</h5>
                <p>
                  Access to, or use of, some areas of the Platform may be
                  subject to additional rules, guidelines, license agreements,
                  user agreements or other terms and conditions (collectively
                  “Special Terms”). You agree to these Special Terms when using
                  particular elements of the Platform. These Special Terms (to
                  the extent that they apply to a specific user) are
                  incorporated to these T&amp;C by this reference. If there is a
                  conflict or inconsistency between the Special Terms for a
                  specific area of the Platform and the remainder of these
                  T&amp;C, the Special Terms will have precedence with respect
                  to access and use of that area of the Platform.
                </p>
              </span>
            </li>
            <li>
              <h5>Definitions</h5>
              <span>
                <p>
                  “Platform” means all Content, Services and software or other
                  technologies underlying the operation of the Site.
                </p>
                <p>
                  “Site” means the website(s) located at www.radivision.com.
                </p>
                <p>
                  “Content” means the webpages, data, messages, text, images,
                  photographs, graphics, audio or video recordings, product
                  descriptions, pricing, and other information or material
                  available from or through the Platform.
                </p>
                <p>
                  “Services” means the forums, discussion groups, chat areas,
                  bulletin boards, blogs, wikis, messaging or e-mail functions,
                  ratings, and other functionalities or services available from
                  or through the Platform, and in connection with which a user
                  can upload, download, email, post, publish, transmit, access
                  or make available or share Content with others.
                </p>

                <p>
                  “Share” or “Sharing” means to display, post, email, upload,
                  transmit, publish, or make available to others.
                </p>
                <p>
                  “Partners” includes the sponsors, advertisers, suppliers,
                  service providers, licensors and other entities that
                  contribute to, or provide services for, the development,
                  financing, and operation of the Platform.
                </p>
                <p>
                  Radivision may make improvements and/or changes on the
                  Platform at any time. All definitions above are intended to
                  apply to the then-current version of the Platform.
                </p>
              </span>
            </li>
            <li>
              <h5>Modifications</h5>
              <span>
                <p>
                  These T&amp;C may be modified from time to time. Radivision
                  will post a notice of such changes on the Platform in
                  accordance with applicable laws. The revised T&amp;C will be
                  effective as indicated in the notice.
                </p>
                <p className="font-weight-bold text-center">
                  CONTINUED USE OF THE PLATFORM AFTER MODIFIED T&amp;C HAVE
                  BECOME EFFECTIVE WILL INDICATE YOUR AGREEMENT TO BE BOUND BY
                  THE MODIFIED T&amp;C WITH RESPECT TO ALL DATA THEN IN OUR
                  CUSTODY OR CONTROL.
                </p>
                <p>
                  Certain provisions of these T&amp;C may be superseded by
                  expressly-designated terms located on particular pages of the
                  Platform. In such circumstances, the expressly-designated
                  legal notice or term will be deemed to be incorporated into
                  these T&amp;C and to supersede those provision(s) of these
                  T&amp;C that are designated as being superseded.
                </p>
              </span>
            </li>
            <li>
              <h5>Minors May Not Use the Platform</h5>
              <span>
                <p>
                  As a condition of your use of the Platform, you warrant that
                  you are not a “minor” as such term is defined in your state or
                  country of residence, and you possess the legal authority to
                  enter into a binding legal obligation.
                </p>
              </span>
            </li>
            <li>
              <h5>Privacy Policy</h5>
              <span>
                <p>
                  We collect information about users when they access or use the
                  Platform. Any personal information collected on the Platform
                  will be treated in accordance with{" "}
                  <a
                    onClick={() =>
                      Router.to("legal", undefined, "privacy-policy")
                    }
                  >
                    Radivision's Privacy Notice
                  </a>
                  . The Privacy Notice is made a part of these T&amp;C by this
                  reference.
                </p>
              </span>
            </li>
            <li>
              <h5>Ownership; Reservation of Rights</h5>
              <span>
                <p>
                  Unless otherwise stated in these T&amp;C, as between you and
                  Radivision, the Platform (including its structure and
                  arrangement) are Radivision's sole property. Radivision and
                  its licensors retain all right, title and interest in the
                  Platform, including all copyright, patent, trademark, trade
                  secrets and all other intellectual property rights
                  (collectively “Intellectual Property Rights”).
                </p>
                <p>
                  Except as otherwise required or limited by applicable law, any
                  reproduction, distribution, modification, retransmission, or
                  publication of any part of the Platform is strictly prohibited
                  without Radivision’s express written consent.
                </p>
              </span>
            </li>
            <li>
              <h5>License</h5>
              <span>
                <p>
                  Radivision grants each user a limited, personal,
                  non-exclusive, non-transferable, non sublicensable license to
                  access and use the Platform only to the extent that the user
                  complies with all provisions of these T&amp;C. Radivision
                  reserves all other rights or licenses not expressly granted.{" "}
                </p>

                <p>
                  You may use, access, download, store, display on your device
                  (computer, smart phone, tablet), view, listen to, play, share
                  and print any Content that is made available on or through the
                  Platform, only for your personal, informational, noncommercial
                  purposes and subject to the following:
                </p>
                <ul>
                  <li>
                    You may not harvest, collect or store information about
                    other users;
                  </li>
                  <li>
                    You may not copy, reproduce, redistribute, modify or alter
                    any aspect of the Platform;
                  </li>
                  <li>
                    You may not create derivative works of, reverse engineer, or
                    exploit any aspect of the Platform;
                  </li>
                  <li>
                    You may not use the Platform for any purpose that is
                    unlawful or prohibited by these T&amp;C;
                  </li>
                  <li>
                    In any permitted copy of the Content, you must conspicuously
                    display all Radivision’s (or Radivision’s licensors’)
                    copyright or other Intellectual Property Right notices as
                    they appear on the Platform;
                  </li>
                  <li>
                    You may not remove, deface, or overprint any notice of
                    copyright, trademark, logo, or other notice of ownership
                    from any originals or copies of the Platform or any third
                    party’s material that you access on or through the Platform.
                  </li>
                </ul>
              </span>
            </li>
            <li>
              <h5>Radivator</h5>
              <span>
                <p>
                  A user may elect to become a “Radivator”, which allows the
                  user to participate in certain activities on the Platform. To
                  do so, the user must sign in on and chose a password and a
                  user name.{" "}
                </p>

                <p>
                  As a condition of your exercise of your rights or privileges
                  as a Radivator, you agree that:
                </p>
                <ul>
                  <li>
                    You are responsible for maintaining the confidentiality of
                    your user name and password;
                  </li>
                  <li>
                    You will not share your user name and/or password, or
                    provide access to the Platform to any other person;
                  </li>
                  <li>
                    You are responsible for all activities that occur through
                    the use of your user name and password;
                  </li>
                  <li>
                    You will notify Radivision immediately of any use of your
                    password(s) or account information that you did not
                    authorize or that is not authorized by these T&amp;C.
                  </li>
                </ul>
                <p>
                  We reserve the right to require that you change your password
                  or restrict your access to the Platform as we deem reasonably
                  necessary.
                </p>
              </span>
            </li>

            <li>
              <h5>Your Security Obligations</h5>
              <span>
                <p>
                  We use reasonable technical, physical and other measures to
                  protect the availability and integrity of the Platform.{" "}
                </p>

                <p>
                  We expect you to use the Platform responsibly. Among other
                  things, you agree not to:
                </p>
                <ul>
                  <li>
                    Access or use the Platform in any manner that could damage,
                    disable, overburden, or impair any of the computer systems,
                    networks or databases that form or operate the Platform;
                  </li>
                  <li>
                    Attempt to gain access to any user accounts or any parts of
                    the Platform, or its computer systems, networks or databases
                    or than your user account;
                  </li>
                  <li>
                    Interfere or attempt to interfere with the proper working of
                    any user account or the Platform or its computer systems,
                    networks or databases;
                  </li>
                  <li>
                    Use any robot, spider, scraper or other automated means to
                    access any user account or the Platform or its computer
                    systems, networks or database.
                  </li>
                </ul>
              </span>
            </li>

            <li>
              <h5>User Submissions</h5>
              <span>
                <p>
                  Certain areas of the Platform (e.g., chat rooms, customer
                  ratings, review areas, community and support forums) may
                  permit users to submit feedback, comments, ratings,
                  information, data, messages, text, software, music, sound,
                  audio, images, photographs, graphics, video, audio, digital
                  recording or recording of a performance (each, a
                  "Submission"). We have no obligation to accept, host, publish,
                  display, or distribute any Submission on or through the
                  Platform. We accept no responsibility or liability with
                  respect to any Submission.{" "}
                </p>

                <p>By sharing a Submission, you agree that:</p>
                <ul>
                  <li>You are solely responsible for your Submissions;</li>
                  <li>Your Submission is considered non-confidential;</li>
                  <li>
                    You waive all moral rights you may have in your Submission
                    and all moral rights of others may have in your Submission
                    are waived to the full extent allowed by law.
                  </li>
                </ul>
                <p>
                  By submitting photographs of the submitter for publishing on
                  the Radivision website and other Radivision media venues,
                  Submitter agrees to indemnify, defend and hold harmless
                  Radivision from any and all claims, actions, damages, and
                  liabilities (excluding, without limitation, attorneys’ fees,
                  costs and expenses) arising
                </p>
                <ul>
                  <li> through Submitter's gross negligence;</li>
                  <li>
                    out of any claim that the materials or deliverables, or any
                    portion thereof, in fact infringes upon or violate any
                    proprietary rights of any third party, including but not
                    limited to patent, copyright and trade secret rights;
                  </li>
                  <li>
                    from a breach or alleged breach of any of Submitter's
                    representations, warranties or agreements herein.
                  </li>
                </ul>
                <h5>No Confidentiality</h5>
                <p>
                  Radivision does not want to receive confidential information
                  from any user through or in connection with the Platform.
                  Notwithstanding anything that you may note or state when
                  sharing your Submissions, any Submission will be received and
                  treated as non-confidential and unrestricted.{" "}
                </p>

                <p>
                  Radivision will not take, and will not be required to take,
                  any steps to safeguard the confidentiality of any information
                  that you share, other than as specified in Radivision's
                  Privacy Policy.
                </p>

                <h5>License Grant</h5>
                <p>
                  By sharing a Submission through the Platform, you grant
                  Radivision on your behalf and on behalf of any person who
                  might have any Intellectual Property Rights in your
                  Submission, an irrevocable, perpetual, worldwide,
                  royalty-free, nonexclusive, transferable, fully sublicensable,
                  license to do any of the following (“License”) for any
                  purpose, including, without limitation for commercial
                  purposes, and in any manner that Radivision deems appropriate,
                  to:
                </p>
                <ul>
                  <li>
                    Use, reproduce, modify, adapt, translate, publish, publicly
                    perform, publicly display, broadcast, transmit, share and
                    distribute the Submission (or any modification thereto), in
                    whole or in part, in any form, medium, or technology now
                    known or later developed;
                  </li>
                  <li>
                    Incorporate the Submission or any modification thereto, in
                    whole or in part, into any material, technology, product, or
                    service that Radivision subsequently distributes, sells or
                    licenses to third parties;
                  </li>
                  <li>
                    Display advertisements in connection with the Submission or
                    use the Submission for advertising and promotional purposes.
                  </li>
                </ul>
                <h5>Representations and Warranties</h5>
                <p>
                  By sharing your Submission through the Platform, you represent
                  and warrant that:
                </p>
                <ul>
                  <li>
                    You own all right, title, and interest, including all
                    Intellectual Property Rights, in and to the Submission or,
                    alternatively, you have acquired all necessary rights in to
                    enable you to grant to Radivision the rights in your
                    Submission as described in these T&amp;C;
                  </li>
                  <li>
                    You have the right and authority to grant the License;
                  </li>
                  <li>
                    Your Submission does not violate any Law, including, without
                    limitation, those governing export control, consumer
                    protection, unfair competition, anti-discrimination, false
                    advertising, and does not infringe the Intellectual Property
                    Rights, privacy rights, or any other legal or moral rights
                    of any third party;
                  </li>
                  <li>
                    Radivision's exercise of the rights granted pursuant to the
                    License will not infringe or otherwise violate any
                    third-party rights;
                  </li>
                  <li>
                    You have paid and will pay in full all license fees,
                    clearance fees, and other financial obligations, of any
                    kind, if any, arising from any use or commercial
                    exploitation of your Submission;
                  </li>
                  <li>
                    You are the individual pictured and/or heard in your
                    Submission or, alternatively, you have obtained permission
                    from each person (including consent from parents or
                    guardians for any individual who is deemed a minor under
                    applicable law) who appears and/or is heard in your
                    Submission, to grant the rights to Radivision as described
                    in these T&amp;C;
                  </li>
                  <li>
                    You are not aware and have not be informed that any
                    information contained in the Submission is false,
                    inaccurate, or misleading;
                  </li>
                  <li>
                    Your Submission does not violate our Acceptable Use Policy
                    (set forth below in the “Acceptable Use” Section);
                  </li>
                  <li>
                    You were not and will not be compensated or granted any
                    similar consideration by any third party for submitting the
                    Submission;
                  </li>
                  <li>
                    Your Submission does not incorporate material from a
                    third-party website, or addresses, email addresses, contact
                    information, or phone numbers of any person (other than your
                    own);
                  </li>
                  <li>
                    Your Submission does not contain any viruses, worms,
                    spyware, adware, or other potentially damaging programs or
                    files; and
                  </li>
                  <li>
                    Your Submission does not contain or constitute any
                    unsolicited or unauthorized advertising, promotional
                    material, junk mail, spam, chain letters, pyramid schemes,
                    or any other form of solicitation.
                  </li>
                </ul>
              </span>
            </li>

            <li>
              <h5>Acceptable Use</h5>
              <span>
                <p>
                  A submission will not be accepted or if initially accepted,
                  may will be taken down if it meets any of the following:
                </p>
                <ul>
                  <li>Is false or misleading;</li>
                  <li>
                    Invades another's privacy or contains personal information
                    of a third party;
                  </li>
                  <li>
                    Contains confidential or sensitive of information or trade
                    secrets of another person or entity;
                  </li>
                  <li>Is vulgar, pornographic, or obscene;</li>
                  <li>
                    Is defamatory, libelous, derogatory, degrading, harassing,
                    or threatening of another or constitutes a personal attack;
                  </li>
                  <li>
                    Promotes bigotry, hatred or harm against any group or
                    individual, or is racially, ethnically, religiously, or
                    otherwise biased or offensive;
                  </li>
                  <li>
                    Violates or infringes, or promotes the violation or
                    infringement of any applicable laws or regulations;
                  </li>
                  <li>
                    Violates or infringes or promotes the violation or
                    infringement of another's rights, including privacy rights,
                    Intellectual Property Rights, or any other legal or moral
                    rights;
                  </li>
                  <li>
                    For which you do not have the right and authority to share
                    or to grant the necessary rights and licenses;
                  </li>
                  <li>
                    Contains a solicitation of funds, goods or services, or
                    promotes or advertises goods or services; or
                  </li>
                  <li>
                    Contains any viruses, Trojan horses, malware, spyware,
                    ransomware, or other components designed to limit or harm
                    the functionality of a computer.
                  </li>
                </ul>
              </span>
            </li>

            <li>
              <h5>Monitoring; Take Down</h5>
              <span>
                <p>
                  Radivision may, but is not obligated to, pre-screen any
                  Submission or monitor any area of the Platform through which
                  Submissions may be submitted and may, in its sole discretion,
                  refuse any Submission for any reason.{" "}
                </p>

                <p>
                  Radivision may remove or block access to any Submission if it
                  determines, in its sole discretion, that the Submission
                  violates these T&amp;C, and in addition to any other rights it
                  has under these T&amp;C, may report you the author of the
                  Submission to the relevant authorities; and/or act to the
                  fullest extent of applicable laws.
                </p>
              </span>
            </li>

            <li>
              <h5>Indemnity</h5>
              <span>
                <p>
                  You agree to indemnify and hold harmless Radivision, its
                  officers, directors, employees and agents (collectively
                  “Radivision Parties”) from and against any and all claims,
                  liabilities, damages, losses, expenses, or fees (including
                  reasonable attorneys' fees, expert’s fees, and court costs)
                  that such Radivision Parties may incur as a result of, or
                  arising from, your Submission (or any Submission provided by
                  anyone using your account credentials), violation of these
                  T&amp;C, or your violation or infringement of any third
                  party’s Intellectual Property Rights, privacy rights, or any
                  other legal or moral rights.
                </p>

                <p>
                  Radivision reserves the right to assume the exclusive defense
                  and control of any matter otherwise subject to indemnification
                  under this section, and, in such case, you agree to cooperate
                  with Radivision’s defense of such claim.
                </p>
              </span>
            </li>
            <li>
              <h5>Errors, Pricing and Ratings</h5>
              <span>
                <p>
                  We do not guarantee the accuracy of any part of the Platform.
                  We reserve the right to correct any error, including any
                  pricing errors on the Platform. If you have made a purchase
                  under an incorrect price, we will offer you the opportunity to
                  keep your purchase at the correct price or we will cancel your
                  purchase without penalty.{" "}
                </p>

                <p>
                  Product ratings displayed on the Platform are intended as only
                  general guidelines; we do not guarantee the accuracy of the
                  rating.
                </p>
              </span>
            </li>
            <li>
              <h5>Disclaimer of Warranty</h5>
              <span>
                <p>
                  <span className="font-weight-bold">
                    THE PLATFORM AND THE SUBMISSIONS ARE PROVIDED ON AN "AS IS"
                    AND "AS AVAILABLE" BASIS, AS A CONVENIENCE TO USERS, AND
                    WITHOUT ANY WARRANTY OF ANY KIND.
                  </span>
                  Radivision is not obligated to provide any technical or other
                  support for the Content.
                </p>

                <p>
                  The Platform is in constant evolution and is modified,
                  adapted, reformatted or rewritten with very limited time for
                  testing or quality assurance. There may be errors, omissions,
                  deficiencies or inconsistencies. Your use of the Platform is
                  at your own discretion and risk.
                </p>

                <p>
                  We disclaim any warranty that (a) the Platform is free of
                  viruses or other harmful component: (b) the Platform will meet
                  your requirements; (c) the Platform will be available without
                  interruptions, will be timely, secure, or error-free; (d) the
                  results that may be obtained from the use of the Platform will
                  be accurate or reliable; or (e) the quality of any Content or
                  Service will meet your expectations.
                </p>

                <p>
                  Submissions posted on the Platform have been developed by
                  users. These users are independent entities, and are not our
                  agents or representatives. The inclusion of any Submission on
                  the Platform does not constitute any endorsement or
                  recommendation of such user or Submission.
                </p>

                <p className="font-weight-bold text-center">
                  Radivision AND ITS PARTNERS DISCLAIM ALL WARRANTIES OF ANY
                  KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED
                  TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
                  PARTICULAR PURPOSE, TITLE AND NON-INFRINGEMENT.
                </p>
              </span>
            </li>
            <li>
              <h5>Limitation of Liability</h5>
              <span>
                <p>
                  Radivision disclaims any liability for the acts, errors,
                  omissions, representations, warranties, breaches or negligence
                  of any Submissions shared by users.{" "}
                </p>

                <p>
                  Radivision disclaims liability for any damage to any device or
                  network, or for loss of data that may result from access to,
                  or use of the Platform. Information published on the Platform
                  may refer to products, programs, or services that are not
                  available in your region.{" "}
                </p>

                <p className="font-weight-bold text-center">
                  IN NO EVENT SHALL Radivision, AFFILIATED COMPANIES, ITS
                  PARTNERS AND THEIR RESPECTIVE OFFICERS, DIRECTORS, EMPLOYEES,
                  OR AGENTS OF ANY SUCH ENTITIES (COLLECTIVELY, "Radivision
                  PARTIES") BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL,
                  SPECIAL, CONSEQUENTIAL OR EXEMPLARY DAMAGES, OR DAMAGES FOR
                  LOSS OF PROFITS, REVENUE, GOODWILL, OR DATA, OR DATA USE OR
                  MISUSE, BUSINESS INTERRUPTION, OR OTHER INTANGIBLE LOSSES,
                  WHETHER ARISING OUT OF OR IN CONNECTION WITH THE PLATFORM OR
                  ANY PRODUCT PURCHASED THROUGH THE PLATFORM (INCLUDING, WITHOUT
                  LIMITATION, USE, INABILITY TO USE, OR RESULTS OF USE),
                  INCURRED BY YOU OR ANY THIRD PARTY, WHETHER IN AN ACTION IN
                  CONTRACT OR TORT, ARISING FROM YOUR ACCESS TO, OR USE OF, THE
                  PLATFORM OR ANY PRODUCT OR SERVICE PROVIDED ON OR THROUGH THE
                  PLATFORM, WHETHER SUCH DAMAGES ARE BASED ON WARRANTY,
                  CONTRACT, TORT, STATUTE, OR ANY OTHER LEGAL THEORY AND EVEN IF
                  ANY Radivision PARTY HAS BEEN ADVISED (OR SHOULD HAVE KNOWN)
                  OF THE POSSIBILITY OF SUCH DAMAGES.
                </p>

                <p className="font-weight-bold text-center">
                  IF, NOTWITHSTANDING THE ABOVE, THE Radivision PARTIES ARE
                  FOUND LIABLE FOR ANY DAMAGES TO YOU IN CONNECTION WITH OR
                  RELATED TO THE PLATFORM OR ANY PURCHASE MADE THROUGH PLATFORM,
                  IN NO EVENT SHALL SUCH DAMAGES EXCEED, IN TOTAL, ONE HUNDRED
                  US DOLLARS (US 100).
                </p>
              </span>
            </li>

            <li>
              <h5>Exclusions and Limitations</h5>
              <span>
                <p>
                  Some jurisdictions do not allow the disclaimer or exclusion of
                  certain warranties or the disclaimer, exclusion or limitation
                  of certain liabilities, therefore, some of the above
                  limitations may not apply to you. To the extent that they are
                  held to be legally invalid, disclaimers, exclusions and
                  limitations set forth in these T&amp;C, including those set
                  forth in sections “Disclaimer of Warranty” and “Limitation of
                  Liability”, do not apply and all other provisions shall remain
                  in full force and effect.{" "}
                </p>

                <p>
                  To the extent the Radivision Parties may not, as a matter of
                  applicable law, disclaim any implied warranty or limit its
                  liabilities, the scope and duration of such warranty and the
                  amount of the Radivision Party's liability shall be limited to
                  the minimum amount permitted under such applicable law.
                </p>
              </span>
            </li>

            <li>
              <h5>Electronic Communications</h5>
              <span>
                <p>
                  When you visit the Platform, or send electronic communications
                  to Radivision, you are communicating with Radivision
                  electronically as defined by applicable laws. We may respond
                  to you by e-mail or by posting notices on the Platform. You
                  agree that all such notices, disclosures, and other
                  communications that we provide to you electronically satisfy
                  any legal requirement that such communications be in writing.
                </p>
              </span>
            </li>
            <li>
              <h5>Links to Third-Party Websites</h5>
              <span>
                <p>
                  The Platform provides links to third party websites and access
                  to material, information, products and services of third
                  parties, including other users, advertisers, affiliates and
                  sponsors of the Platform (collectively “Third Party Website”).
                  If you use these links, you will leave the Platform, and these
                  T&amp;C will not apply to your use of any such Third-party
                  Websites. You will be deemed subject to the terms and
                  conditions and privacy policies of those Third-party Websites.
                </p>

                <p>
                  If you decide to access or use any of these Third-party
                  Websites, you do so entirely at your own risk. Radivision does
                  not control and is not responsible for these Third Party
                  Websites or for any changes or updates thereto. Radivision
                  does not endorse or make any representations about such
                  Third-party Websites, or any information or results that may
                  be obtained from using them.
                </p>
              </span>
            </li>

            <li>
              <h5>Linking to the Platform</h5>
              <span>
                <p>
                  We allow users to create links to the Platform from other
                  websites. Unless an express agreement states otherwise, you
                  must comply with the following terms and all applicable laws
                  if you provide anyone with a link to the Platform:
                </p>
                <ul>
                  <li>
                    You may link to, but may not copy, frame, or create a
                    browser or border environment around the Platform or any
                    part thereof;
                  </li>
                  <li>
                    You may only use and reproduce our Radivision logo or
                    trademark if you follow our trademark and logo usage policy;
                  </li>
                  <li>
                    You may not imply that Radivision endorses or is affiliated
                    with any third party website or any products, services, or
                    content available through that website;
                  </li>
                  <li>
                    You may not misrepresent your relationship with Radivision;
                  </li>
                  <li>
                    You may not present false or misleading information about
                    Radivision, its Platform or any product or services
                    available through the Platform;
                  </li>
                  <li>
                    The website must meet the requirements of our Acceptable Use
                    Policy; and
                  </li>
                  <li>
                    The website must contain only material that is appropriate
                    for all age groups.
                  </li>
                </ul>
              </span>
            </li>

            <li>
              <h5>Claims of Copyright Infringements</h5>
              <span>
                <p>
                  If you believe that your copyrighted work has been copied and
                  is accessible on the Platform in a way that constitutes
                  copyright infringement, please contact our Copyright Agent as
                  explained in our Procedure for Making Claims of Copyright
                  Infringements
                </p>
              </span>
            </li>

            <li>
              <h5>Trademarks</h5>
              <span>
                <p>
                  “Radivision”, “Radivator”, “Radivision” and “Entre-tainment”
                  are trademarks of Radivision and/or its affiliates. Other
                  names appearing on the Platform may be trademarks of their
                  respective owners.
                </p>
              </span>
            </li>
            <li>
              <h5>Export Restrictions/Legal Compliance</h5>
              <span>
                <p>
                  Radivision administers the Platform from its offices in Menlo
                  Park, CA. SRadivision makes no representation that the
                  Platform is appropriate or available for use outside the
                  United States. If you reside in a country or territory where
                  the Content is illegal or restricted, you may not access the
                  Platform.{" "}
                </p>

                <p>
                  In addition, the Platform and the underlying information or
                  technology may not be accessed from, or downloaded, provided
                  or made available, directly or indirectly, into any country
                  subject to U.S. trade sanctions, (Supplement No. 1 to Part
                  740, Export Administration Regulations, Country Group E:1), to
                  individuals or entities controlled by such countries, or to
                  nationals or residents of such countries other than nationals
                  who are lawfully admitted permanent residents of countries not
                  subject to such sanctions.{" "}
                </p>

                <p>
                  By agreeing to these T&amp;C, you represent and warrant that
                  you are not located in, under the control of, or a national or
                  resident of, any such country or on any such list, and you
                  agree to comply with all export and re-export laws,
                  restrictions and regulations of the US Department of Commerce
                  and other United States or foreign applicable agency or
                  authority, in connection with your use of the Platform.
                </p>
              </span>
            </li>
            <li>
              <h5>Termination of Use</h5>
              <span>
                <p>
                  At any time and in its sole discretion, Radivision may,
                  without prior notice, and for any reason discontinue providing
                  or limit access to any or all areas of the Platform; or
                  terminate, limit, block or suspend a user’s access to, or use
                  of, the Platform;
                </p>

                <p>
                  Radivision will not be liable to any user or any third-party
                  for any termination or limitation of the user’s access to, or
                  use of, the Platform, or for the removal of any of the user’s
                  Submission(s).{" "}
                </p>

                <p>
                  The termination of a user’s access to the Platform results in
                  the termination of any right or license granted to the user
                  with respect to the Platform, and the user must immediately
                  destroy any downloaded or printed Content or third party’s
                  Submissions, and any copies thereof.
                </p>
              </span>
            </li>

            <li>
              <h5>Entire Agreement</h5>
              <span>
                <p>
                  These T&amp;C constitute the entire, exclusive and final
                  statement of the agreement between you and Radivision respect
                  to the subject matter hereof, superseding any prior agreements
                  or negotiations between you and Radivision with respect to the
                  same. The Radivision Parties are third party beneficiaries
                  with respect to the provisions in these T&amp;C that reference
                  them.
                </p>
              </span>
            </li>
            <li>
              <h5>Waiver and Severability</h5>
              <span>
                <p>
                  Radivision’s failure to exercise or enforce any provision in
                  these T&amp;C will not constitute a waiver of such provision.
                  If any provision of these T&amp;C is found to be
                  unenforceable, such part or provision may be modified to make
                  these T&amp;C as modified legal and enforceable. The balance
                  of these T&amp;C shall not be affected.
                </p>
              </span>
            </li>
            <li>
              <h5>Applicable Laws</h5>
              <span>
                <p>
                  All matters relating to your access to, and use of, the
                  Platform, or the Submissions shared on the Platform shall be
                  governed by U.S. federal law or the laws of the State of
                  California as applied to agreements made, entered into, and
                  performed entirely in the State of California by California
                  State residents, notwithstanding your actual place of
                  residence or business. The United Nations Convention on
                  Contracts for the International Sale of Goods and the Uniform
                  Computer Information Transactions Act shall not apply to these
                  T&amp;C.
                </p>

                <p>
                  Any legal action or proceeding relating to your use of, the
                  Platform shall be brought in a state or federal court in San
                  Mateo County, California. You and Radivision agree to submit
                  to the exclusive jurisdiction of, and agree that venue is
                  proper in, these courts for such purpose.
                </p>
              </span>
            </li>

            <li>
              <h5>Miscellaneous </h5>
              <span>
                <p>
                  The failure of Radivision to exercise or enforce any right or
                  provision of these T&amp;C shall not constitute a waiver of
                  such right or provision. If any provision of these T&amp;C is
                  found by a court of competent jurisdiction to be invalid, you
                  agree that the court should endeavor to give effect to the
                  intentions of Radivision and you as reflected in the
                  provision, and that the other provisions of these T&amp;C will
                  remain in full force and effect.
                </p>

                <p>
                  The section titles in these T&amp;C are for convenience only
                  and have no legal or contractual effect.
                </p>

                <p>
                  These T&amp;C will remain in full force and effect
                  notwithstanding any termination of your use of the Platform.
                </p>

                <p>
                  These T&amp;C will be interpreted without application of any
                  strict construction in favor of or against you or Radivision.
                </p>

                <p>
                  These T&amp;C, and any rights and licenses granted hereunder,
                  may not be transferred or assigned by you, but may be assigned
                  by Radivision without restriction.
                </p>
              </span>
            </li>
            <li>
              <h5>Contact Information</h5>
              <span>
                <p>
                  California residents may also reach the Complaint Assistance
                  Unit of the Division of Consumer Services of the California
                  Department of Consumer Affairs by postal mail at 1625 North
                  Market Blvd., Sacramento, CA 95834 or by telephone at{" "}
                  <a href="tel: 1-916-445-1254">+1 (916) 445-1254</a> or{" "}
                  <a href="tel: 1-800-952-5210">+1 (800) 952-5210</a> or Hearing
                  Impaired at TDD{" "}
                  <a href="tel: 1-800-326-2297">+1 (800) 326-2297</a> or TDD{" "}
                  <a href="tel: 1-916-322-1700">+1 (916) 322-1700</a>.
                </p>
              </span>
            </li>
          </ol>
        </div>
      </div>
    );
  }
}
